import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  Platform,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Dimensions,
} from 'react-native';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { useTheme } from '../../../../../theme/useTheme';
import DLOButton from '../../DLOButton';
import { createApiClient } from '../../../../../api/apiClient';
import DragHandleIcon from './DragHandleIcon';
import EditZoneModal from './EditZoneModal';
import SaveResultModal from './SaveResultModal';
import TableSubHeader from './TableSubHeader';
import TableMainHeader from './TableMainHeader';
import { styles } from './styles';
import {
  ZoneRow,
  AvailableZone,
  NonDSDVolumeZoneDistributionByDayProps
} from './types';
import {
  getAvailableZonesFromData,
  isAPIDataFormat,
  convertAPIDataToZoneRows
} from './dataUtils';
import {
  formatPercentageValue,
  formatKCPHValue
} from './formatUtils';
import {
  formatNumberWithSeparators,
  formatVolumeDisplayValue
} from '../../../utils/numberFormatUtils';
import {
  validateDecimalInput,
  validateKCPHInput,
  isRestoreValueCorrect
} from './validationUtils';
import {
  getRestoreValues,
  areAllRestoreValuesCorrect
} from './calculationUtils';
import { getResponsiveStyles } from './uiUtils';

interface NonDSDVolumeZoneDistributionByDayPropsWithDisable extends NonDSDVolumeZoneDistributionByDayProps {
  disabled?: boolean;
}

const NonDSDVolumeZoneDistributionByDay: React.FC<NonDSDVolumeZoneDistributionByDayPropsWithDisable> = ({
  data,
  value,
  onChange,
  onAddZone,
  onSave,
  title = "NON DSD ZONE VOLUME DISTRIBUTION BY DAY",
  style,
  type = 'NON-DSD', // Default to NON-DSD type
  context = 'location-settings', // Default to location-settings for backward compatibility
  isLoading = false, // Default to false
  isSaved: externalIsSaved, // Rename to avoid conflict
  displayAsPercentage = true, // Default to true for backward compatibility
  disabled = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  // Inline light colors are not rewritten by the global dark-mode stylesheet sweep,
  // so disabled override inputs need an explicit dark surface here.
  const darkOverrideInputStyle = isDark
    ? {
        backgroundColor: disabled ? '#1A1A1A' : colors.background.elevated,
        borderColor: colors.border.default,
      }
    : null;

  // Redux selectors
  const selectedLocation = useSelector((state: RootState) => state.appData.selectedLocation);
  const periodWeek = useSelector((state: RootState) => state.appData.periodWeek);

  // Helper function to format display values based on displayAsPercentage flag
  const formatDisplayValue = (value: string): string => {
    return formatVolumeDisplayValue(value, displayAsPercentage);
  };

  const [zoneData, setZoneData] = useState<ZoneRow[]>(() => {
    // Use controlled value if provided
    if (value && value.length > 0) {
      return value;
    }
    
    if (data.length > 0 && isAPIDataFormat(data)) {
      // Convert API data to ZoneRow format with proper mapping
      return convertAPIDataToZoneRows(data);
    } else if (data.length > 0) {
      // Data is already in ZoneRow format
      return data as ZoneRow[];
    }
    
    // Create initial data based on enabled zones (fallback)
    const enabledZones = type === 'NON-DSD' 
      ? [
          { id: 'transfull', name: 'TRANSFULL' },
          { id: 'transpick', name: 'TRANSPICK' },
          { id: 'replen', name: 'REPLEN' },
          { id: 'receiving', name: 'RECEIVING' },
        ]
      : [
          { id: 'geo', name: 'GEO' },
          { id: 'bulk', name: 'BULK' },
          { id: 'full', name: 'FULL' },
          { id: 'chilled', name: 'CHILLED' },
        ];
    
    return enabledZones.map(zone => ({
      id: zone.id,
      zoneName: zone.name,
      percentages: {
        sun: { default: displayAsPercentage ? '0.00%' : '0.00', override: displayAsPercentage ? '0.00%' : '0.00' },
        mon: { default: displayAsPercentage ? '0.00%' : '0.00', override: displayAsPercentage ? '0.00%' : '0.00' },
        tue: { default: displayAsPercentage ? '0.00%' : '0.00', override: displayAsPercentage ? '0.00%' : '0.00' },
        wed: { default: displayAsPercentage ? '0.00%' : '0.00', override: displayAsPercentage ? '0.00%' : '0.00' },
        thu: { default: displayAsPercentage ? '0.00%' : '0.00', override: displayAsPercentage ? '0.00%' : '0.00' },
        fri: { default: displayAsPercentage ? '0.00%' : '0.00', override: displayAsPercentage ? '0.00%' : '0.00' },
        sat: { default: displayAsPercentage ? '0.00%' : '0.00', override: displayAsPercentage ? '0.00%' : '0.00' },
      },
      kcph: { default: '0.00', override: '0.00' },
    }));
  });

  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const [isEditZoneModalVisible, setIsEditZoneModalVisible] = useState(false);
  const [availableZones, setAvailableZones] = useState<AvailableZone[]>(() => {
    const apiZones = getAvailableZonesFromData(data, type);
    const enabledZoneIds = data.length > 0 
      ? (isAPIDataFormat(data) 
          ? [...new Set(data.map(item => item.VP_Zone.toLowerCase()))]
          : data.map(item => item.id))
      : ['transfull', 'transpick', 'replen', 'receiving'];
    
    return apiZones.map(zone => ({
      id: zone.id,
      name: zone.name,
      enabled: enabledZoneIds.includes(zone.id),
    }));
  });

  // Store original data for restoration
  const [originalData, setOriginalData] = useState<ZoneRow[]>([]);
  
  // Store complete original data to preserve zone values when zones are removed and re-added
  const [completeOriginalData, setCompleteOriginalData] = useState<ZoneRow[]>([]);

  // Save operation states
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveModalMessage, setSaveModalMessage] = useState('');
  const [saveModalType, setSaveModalType] = useState<'success' | 'error'>('success');
  
  // Track if user has made any changes to enable save button
  const [hasUserMadeChanges, setHasUserMadeChanges] = useState(false);

  // Track which row is highlighted for restore
  const [highlightedRowIds, setHighlightedRowIds] = useState<string[]>([]);
  
  // Track which ZONES have been modified by the user IN THIS SESSION (zone-level tracking for NON-DSD)
  const [modifiedZonesInSession, setModifiedZonesInSession] = useState<Set<string>>(new Set());
  
  // Track which zones have pre-existing overrides from the backend
  const [zonesWithBackendOverrides, setZonesWithBackendOverrides] = useState<Set<string>>(new Set());

  // Track if we're updating from external value to prevent infinite loops
  const isUpdatingFromValue = useRef(false);

  // Helper function to update both zoneData and completeOriginalData
  const updateZoneData = (newData: ZoneRow[] | ((prev: ZoneRow[]) => ZoneRow[])) => {
    let updatedData: ZoneRow[];
    
    if (typeof newData === 'function') {
      updatedData = newData(zoneData);
    } else {
      updatedData = newData;
    }
    
    // Update current zone data
    setZoneData(updatedData);
    
    // Also update the complete original data to preserve user changes
    // Merge with existing complete original data to maintain zones that aren't currently visible
    setCompleteOriginalData(prevCompleteData => {
      const updatedCompleteData = [...prevCompleteData];
      
      // Update existing zones or add new ones
      updatedData.forEach(updatedRow => {
        const existingIndex = updatedCompleteData.findIndex(row => row.id === updatedRow.id);
        if (existingIndex >= 0) {
          // Update existing zone data
          updatedCompleteData[existingIndex] = updatedRow;
        } else {
          // Add new zone data
          updatedCompleteData.push(updatedRow);
        }
      });
      
      return updatedCompleteData;
    });
  };

  // Controlled component pattern: sync value prop with internal state
  useEffect(() => {
    if (value && JSON.stringify(value) !== JSON.stringify(zoneData)) {
      isUpdatingFromValue.current = true;
      updateZoneData(value);
      // Reset the flag after state update
      setTimeout(() => {
        isUpdatingFromValue.current = false;
      }, 0);
    }
  }, [value]);

  // Controlled component pattern: call onChange when internal state changes
  // Only call onChange if the data actually changed and it's not from external value sync
  useEffect(() => {
    if (onChange && !isUpdatingFromValue.current) {
      onChange(zoneData);
    }
  }, [zoneData, onChange]);

  // Update screen dimensions on change
  useEffect(() => {
    const onChange = (result: any) => {
      setScreenData(result.window);
    };

    const subscription = Dimensions.addEventListener('change', onChange);
    return () => subscription?.remove();
  }, []);

  // Update zones when API data changes
  useEffect(() => {
    if (data && data.length > 0) {
      const apiZones = getAvailableZonesFromData(data, type);
      const enabledZoneIds = isAPIDataFormat(data)
        ? [...new Set(data.map(item => item.VP_Zone.toLowerCase()))]
        : data.map(item => item.id);
      
      // Update available zones
      setAvailableZones(apiZones.map(zone => ({
        id: zone.id,
        name: zone.name,
        enabled: enabledZoneIds.includes(zone.id),
      })));

      // Update zone data with proper API mapping
      if (isAPIDataFormat(data)) {
        const convertedZoneData = convertAPIDataToZoneRows(data);
        updateZoneData(convertedZoneData);
      } else {
        // Data is already in ZoneRow format
        updateZoneData(data as ZoneRow[]);
      }
      
      // Reset user changes flag when new data is loaded
      setHasUserMadeChanges(false);
    }
  }, [data]);

  // Store original data when data changes for restore functionality
  useEffect(() => {
    if (data && data.length > 0) {
      if (isAPIDataFormat(data)) {
        const convertedData = convertAPIDataToZoneRows(data);
        setOriginalData(convertedData);
        // Also initialize complete original data if it's empty
        if (completeOriginalData.length === 0) {
          console.log('Setting complete original data for NON-DSD table:', convertedData);
          setCompleteOriginalData([...convertedData]);
        }
        
        // Detect which ZONES have actual overrides from the API (zone-level tracking for NON-DSD)
        const zonesWithOverrides = new Set<string>();
        convertedData.forEach(row => {
          if (row.__actualOverrides) {
            // Check if ANY day in this zone has an override
            const hasAnyOverride = Object.entries(row.__actualOverrides).some(([key, hasOverride]) => {
              return hasOverride === true && key !== 'kcph';
            });
            if (hasAnyOverride) {
              console.log(`Zone ${row.zoneName}: has pre-existing backend overrides`);
              zonesWithOverrides.add(row.zoneName.toUpperCase());
            }
          }
        });
        console.log('Zones with PRE-EXISTING overrides from API:', Array.from(zonesWithOverrides));
        setZonesWithBackendOverrides(zonesWithOverrides);
        // Reset session modifications to empty
        console.log('Resetting modifiedZonesInSession to empty set (no user modifications yet in this session)');
        setModifiedZonesInSession(new Set());
        
      } else {
        const dataCopy = JSON.parse(JSON.stringify(data as ZoneRow[]));
        setOriginalData(dataCopy);
        // Also initialize complete original data if it's empty
        if (completeOriginalData.length === 0) {
          console.log('Setting complete original data for NON-DSD table:', dataCopy);
          setCompleteOriginalData([...dataCopy]);
        }
        
        // Detect which ZONES have actual overrides from the data (zone-level tracking for NON-DSD)
        const zonesWithOverrides = new Set<string>();
        dataCopy.forEach((row: ZoneRow) => {
          if (row.__actualOverrides) {
            // Check if ANY day in this zone has an override
            const hasAnyOverride = Object.entries(row.__actualOverrides).some(([key, hasOverride]) => {
              return hasOverride === true && key !== 'kcph';
            });
            if (hasAnyOverride) {
              console.log(`Zone ${row.zoneName}: has pre-existing backend overrides`);
              zonesWithOverrides.add(row.zoneName.toUpperCase());
            }
          }
        });
        console.log('Zones with PRE-EXISTING overrides from data:', Array.from(zonesWithOverrides));
        setZonesWithBackendOverrides(zonesWithOverrides);
        // Reset session modifications to empty
        console.log('Resetting modifiedZonesInSession to empty set (no user modifications yet in this session)');
        setModifiedZonesInSession(new Set());
      }
    }
  }, [data, completeOriginalData.length]);

  // Sync availableZones with current zoneData
  useEffect(() => {
    setAvailableZones(prev => prev.map(zone => ({
      ...zone,
      enabled: zoneData.some(zd => zd.id === zone.id)
    })));
  }, [zoneData]);

  // Track changed override values for bold styling
  useEffect(() => {
    if (context === 'schedule-settings' ? externalIsSaved : isSaved) {
      // After save, reset __changed flags for all rows
      setZoneData(prev => prev.map(row => ({ ...row, __changed: {} })));
      setHighlightedRowIds([]); // Clear row highlighting when saved
    }
  }, [externalIsSaved, isSaved, context]);

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Helper function to check if any zone has override values for a specific day
  const hasOverrideValuesForDay = (dayKey: keyof ZoneRow['percentages']): boolean => {
    return zoneData.some(row => {
      const overrideValue = row.percentages[dayKey].override;
      const defaultValue = row.percentages[dayKey].default;
      return overrideValue !== defaultValue && overrideValue !== '0.00%' && overrideValue !== '0%' && overrideValue !== '0.00' && overrideValue !== '';
    });
  };

  // Helper function to check if any zone has override values for KCPH
  const hasOverrideValuesForKCPH = (): boolean => {
    return zoneData.some(row => {
      const overrideValue = row.kcph.override;
      const defaultValue = row.kcph.default;
      return overrideValue !== defaultValue && overrideValue !== '0.00' && overrideValue !== '';
    });
  };

  const updatePercentage = (rowId: string, day: keyof ZoneRow['percentages'], type: 'default' | 'override', value: string) => {
    if (disabled) return;
    if (validateDecimalInput(value)) {
      // Strip commas so the stored value is a plain number string
      value = value.replace(/,/g, '');
      setIsSaved(false);
      setHasUserMadeChanges(true);
      
      updateZoneData(prev => prev.map(row => {
        if (row.id === rowId) {
          // Track changed override value for bold
          let changed = row.__changed || {};
          let actualOverrides = row.__actualOverrides || {};
          
          if (type === 'override') {
            const original = row.percentages[day].default;
            changed = { ...changed, [day]: value !== original };
            // Mark as actual override when user manually changes it
            actualOverrides = { ...actualOverrides, [day]: value !== original };
            
            // Track that this ZONE has been modified in this session (zone-level tracking for NON-DSD)
            setModifiedZonesInSession(prev => new Set(prev).add(row.zoneName.toUpperCase()));
          }
          
          return {
            ...row,
            percentages: {
              ...row.percentages,
              [day]: {
                ...row.percentages[day],
                [type]: value,
              },
            },
            __changed: changed,
            __actualOverrides: actualOverrides,
          };
        }
        return row;
      }));
    }
  };

  const updateKCPH = (rowId: string, type: 'default' | 'override', value: string) => {
    if (disabled) return;
    if (validateKCPHInput(value)) {
      // Strip commas so the stored value is a plain number string
      value = value.replace(/,/g, '');
      setIsSaved(false);
      setHasUserMadeChanges(true);
      updateZoneData(prev => prev.map(row => {
        if (row.id === rowId) {
          // Track changed override value for bold
          let changed = row.__changed || {};
          if (type === 'override') {
            const original = row.kcph.default;
            changed = { ...changed, kcph: value !== original };
          }
          return {
            ...row,
            kcph: {
              ...row.kcph,
              [type]: value,
            },
            __changed: changed,
          };
        }
        return row;
      }));
    }
  };

  // Restore functions to reset override values to default values
  const handleRestore = (columnKey: string) => {
    if (disabled) return;
    setIsSaved(false);
    setHasUserMadeChanges(true);
    if (columnKey === 'kcph') {
      updateZoneData(prev => prev.map(row => {
        return {
          ...row,
          kcph: {
            ...row.kcph,
            override: row.kcph.default,
          },
        };
      }));
    } else {
      const dayKey = columnKey as keyof ZoneRow['percentages'];
      updateZoneData(prev => prev.map(row => {
        return {
          ...row,
          percentages: {
            ...row.percentages,
            [dayKey]: {
              ...row.percentages[dayKey],
              override: row.percentages[dayKey].default,
            },
          },
        };
      }));
    }
  };

  // API call function for saving location settings
  const saveLocationSettings = async () => {
    if (disabled) return;
    setIsSaving(true);
    try {
      console.log('Initiating save operation for context:', context);
      
      // Validate required Redux data
      if (!selectedLocation?.id) {
        throw new Error('No location selected. Please select a location first.');
      }
      
      // For location-settings, validate period and week from Redux
      if (context === 'location-settings') {
        if (!periodWeek?.data?.period || !periodWeek?.data?.week) {
          throw new Error('Period and week information not available.');
        }
      }
      
      // ===== COMMON PAYLOAD BUILDING LOGIC FOR BOTH CONTEXTS =====
      
      // Prepare the updates array
      const updates: any[] = [];
      
      // Required zones based on type (as per API error message)
      const requiredZones = type === 'DSD' 
        ? ['GEO', 'BAY', 'BULK', 'FULL', 'LAYER', 'CHILLED', 'FSV']
        : ['TRANSFULL', 'TRANSPICK', 'REPLEN', 'RECEIVING'];
      
      // Convert day names to numbers
      const dayMapping = {
        sun: 1, mon: 2, tue: 3, wed: 4, thu: 5, fri: 6, sat: 7
      };
      
      // Use original data to ensure all zones (including hidden ones) are included in the API call
      const allZonesData = originalData.length > 0 ? originalData : zoneData;
      
      // For zones that are currently visible, use current data; for hidden zones, use original data
      const getZoneDataForSave = (zoneName: string) => {
        // First try to find in current visible zones (user may have modified these)
        const currentZone = zoneData.find(zone => zone.zoneName.toUpperCase() === zoneName.toUpperCase());
        if (currentZone) {
          return currentZone;
        }
        
        // If not in current visible zones, use original data (hidden/removed zones)
        const originalZone = originalData.find(zone => zone.zoneName.toUpperCase() === zoneName.toUpperCase());
        if (originalZone) {
          return originalZone;
        }
        
        // If neither found, create a default zone (shouldn't happen but safety net)
        return null;
      };
      
      // Build updates for all days and all zones (including hidden ones)
      // Combine session modifications with pre-existing backend overrides (ZONE-LEVEL for NON-DSD)
      const allModifiedZones = new Set([...modifiedZonesInSession, ...zonesWithBackendOverrides]);
      
      // Log which zones were modified for debugging
      console.log('==== SAVE OPERATION START (NON-DSD) ====');
      console.log('Zones modified in THIS session:', Array.from(modifiedZonesInSession));
      console.log('Zones with PRE-EXISTING backend overrides:', Array.from(zonesWithBackendOverrides));
      console.log('Combined modified zones (will send override data):', Array.from(allModifiedZones));
      console.log('Current zoneData:', zoneData.map(z => ({
        zoneName: z.zoneName,
        __actualOverrides: z.__actualOverrides
      })));
      console.log('==============================');
      
      // Process all required zones, not just visible ones
      requiredZones.forEach(zoneName => {
        const zoneDataForSave = getZoneDataForSave(zoneName);
        
        // Check if this ZONE should send override data (either modified in session OR has backend override)
        const shouldSendOverride = allModifiedZones.has(zoneName.toUpperCase());
        console.log(`Zone ${zoneName}: shouldSendOverride = ${shouldSendOverride}`);
        
        if (zoneDataForSave) {
          // Process all days for this zone
          Object.entries(dayMapping).forEach(([dayKey, dayNum]) => {
            const dayData = zoneDataForSave.percentages[dayKey as keyof ZoneRow['percentages']];
            
            // Determine if we should send override data for this zone
            let overridePct: number | null;
            
            if (shouldSendOverride) {
              // Zone was modified in session OR has pre-existing backend override - send override values
              const percentageValue = parseFloat(dayData.override.replace('%', '')).toFixed(2);
              const decimalValue = (parseFloat(percentageValue) / 100).toFixed(2);
              overridePct = Number(decimalValue);
              console.log(`  ${zoneName} day ${dayNum}: shouldSendOverride=true, setting overridePct=${overridePct} (from override="${dayData.override}")`);
            } else {
              // Zone was NOT modified and has NO backend override - send null for override_pct
              overridePct = null;
              console.log(`  ${zoneName} day ${dayNum}: shouldSendOverride=false, setting overridePct=null (override="${dayData.override}" will be ignored)`);
            }
            
            const overrideKcph = parseFloat(zoneDataForSave.kcph.override);
            
            updates.push({
              vpZone: zoneName.toUpperCase(),
              dayNum: dayNum,
              overridePct: overridePct,
              overrideKcph: overrideKcph,
            });
          });
        } else {
          // Create default entries for missing zone (safety net) - all days with null
          Object.values(dayMapping).forEach(dayNum => {
            updates.push({
              vpZone: zoneName.toUpperCase(),
              dayNum: dayNum,
              overridePct: null,
              overrideKcph: 0.0,
            });
          });
        }
      });
      
      console.log('Final updates array:', updates.map(u => ({ vpZone: u.vpZone, dayNum: u.dayNum, overridePct: u.overridePct })));
      console.log(`Total updates sent: ${updates.length} (should be ${requiredZones.length * 7} for all zones × all days)`);
      
      // ===== CONTEXT-SPECIFIC API CALL =====
      
      if (context === 'schedule-settings') {
        // For schedule-settings, call the external onSave callback with the prepared updates
        // The parent component will handle the API call with the correct endpoint
        console.log('Schedule-settings context: calling external onSave with updates array');
        setHasUserMadeChanges(false);
        // Pass the updates to the parent via the callback
        onSave?.(updates);
        return; // Exit early - parent will handle the API call
      }
      
      // ===== LOCATION-SETTINGS API CALL =====
      
      // Prepare request body
      const requestBody = {
        loc_id: selectedLocation?.id || "1016",
        updates: updates
      };
      
      // Make API call
      const api = createApiClient();
      const response = await api.put(`/dlo/LocationSettings?type=${type}`, requestBody);
      
      if (response.data.success) {
        setSaveModalMessage('NON DSD Zone Volume table save action completed successfully');
        setSaveModalType('success');
        setShowSaveModal(true);
        setIsSaved(true);
        setHasUserMadeChanges(false);
        
        // After successful save, update backend overrides to include session modifications
        setZonesWithBackendOverrides(new Set([...zonesWithBackendOverrides, ...modifiedZonesInSession]));
        setModifiedZonesInSession(new Set()); // Clear session modifications after successful save
        
        onSave?.();
      } else {
        throw new Error(response.data.message || 'Save operation failed');
      }
    } catch (error: any) {
      console.error('Save error:', error);
      setSaveModalMessage(error.response?.data?.message || error.message || 'An error occurred while saving');
      setSaveModalType('error');
      setShowSaveModal(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle percentage formatting on blur (only for override percentage fields)
  const handlePercentageBlur = (rowId: string, day: keyof ZoneRow['percentages'], type: 'default' | 'override', value: string) => {
    if (type === 'override' && displayAsPercentage) {
      const formattedValue = formatPercentageValue(value);
      if (formattedValue !== value) {
        updatePercentage(rowId, day, type, formattedValue);
      }
    }
  };

  // Handle KCPH formatting on blur (only for override KCPH fields)
  const handleKCPHBlur = (rowId: string, type: 'default' | 'override', value: string) => {
    if (type === 'override') {
      const formattedValue = formatKCPHValue(value);
      if (formattedValue !== value) {
        updateKCPH(rowId, type, formattedValue);
      }
    }
  };

  const responsiveStyles = getResponsiveStyles();

  // Calculate totals for restore row based on override values
  const restoreValues = getRestoreValues(zoneData, displayAsPercentage);

  // Calculate KCPH restore value based on sum of all override KCPH values
  // Commented out as KCPH total display is not required for business
  /*
  const kcphRestoreValue = getKCPHRestoreValue(zoneData);
  */

  // Check if all zones have override values that sum to exactly 100% (green badges)
  const areAllRestoreBadgesGreen = () => {
    // Commented out as per new requirement to not validate total = 100%
    /*
    return zoneData.every(row => {
      const zoneOverrideTotal = dayLabels.reduce((sum, day) => {
        const dayKey = day.toLowerCase() as keyof ZoneRow['percentages'];
        const overrideValue = row.percentages[dayKey].override;
        
        // Only include non-zero, non-empty override values
        if (overrideValue && overrideValue !== '0.00%' && overrideValue !== '0%' && overrideValue !== '') {
          const numericValue = parseFloat(overrideValue.replace('%', '')) || 0;
          return sum + numericValue;
        }
        return sum;
      }, 0);
      
      // Check if this zone's total is exactly 100
      return Math.abs(zoneOverrideTotal - 100) < 0.01;
    });
    */
    // New requirement: just ensure all override values are valid (not empty or invalid)
    return zoneData.every(row => {
      return dayLabels.every(day => {
        const dayKey = day.toLowerCase() as keyof ZoneRow['percentages'];
        const overrideValue = row.percentages[dayKey].override;
        return validateDecimalInput(overrideValue);
      });
    });
  };

  const isSaveEnabled = displayAsPercentage 
    ? (areAllRestoreBadgesGreen() && hasUserMadeChanges)
    : hasUserMadeChanges;

  // Handle Add Zone button click
  const handleAddZoneClick = () => {
    setIsEditZoneModalVisible(true);
  };

  // Handle zone toggle in modal
  const handleZoneToggle = (zoneId: string, enabled: boolean) => {
    setAvailableZones(prev => prev.map(zone => 
      zone.id === zoneId ? { ...zone, enabled } : zone
    ));
  };

  // Handle modal save
  const handleModalSave = () => {
    const enabledZones = availableZones.filter(zone => zone.enabled);
    console.log('Saving NON-DSD modal with enabled zones:', enabledZones.map(z => z.name));
    console.log('Complete original data available:', completeOriginalData.map(r => ({ id: r.id, zoneName: r.zoneName, percentages: r.percentages })));
    
    // Update zoneData based on enabled zones
    const newZoneData: ZoneRow[] = enabledZones.map(zone => {
      // First check if zone already exists in current data to preserve values
      let existingZone = zoneData.find(zd => zd.id === zone.id);
      let dataSource = 'current';
      
      // If not found in current data, check the complete original data to restore previous values
      if (!existingZone) {
        existingZone = completeOriginalData.find(zd => zd.id === zone.id);
        dataSource = 'original';
      }
      
      if (existingZone) {
        console.log(`Restoring NON-DSD zone ${zone.name} from ${dataSource} data:`, existingZone.percentages);
        return existingZone;
      }
      
      console.log(`Creating new NON-DSD zone ${zone.name} with default values`);
      // Create new zone with default values
      return {
        id: zone.id,
        zoneName: zone.name,
        percentages: {
          sun: { default: '0.00%', override: '0.00%' },
          mon: { default: '0.00%', override: '0.00%' },
          tue: { default: '0.00%', override: '0.00%' },
          wed: { default: '0.00%', override: '0.00%' },
          thu: { default: '0.00%', override: '0.00%' },
          fri: { default: '0.00%', override: '0.00%' },
          sat: { default: '0.00%', override: '0.00%' },
        },
        kcph: { default: '0.00', override: '0.00' },
      };
    });
    
    updateZoneData(newZoneData);
    setIsEditZoneModalVisible(false);
    
    // Call the onAddZone callback if provided
    onAddZone?.();
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    // Reset available zones to match current zone data
    setAvailableZones(prev => prev.map(zone => ({
      ...zone,
      enabled: zoneData.some(zd => zd.id === zone.id)
    })));
    setIsEditZoneModalVisible(false);
  };

  return (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={Platform.OS === 'web'}
      contentContainerStyle={styles.scrollContainer}
    >
      <View style={[
        styles.container, 
        { minWidth: responsiveStyles.containerMinWidth },
        style
      ]}>
        {/* Main Header */}
        <TableMainHeader
          title={title}
          onEditZoneClick={handleAddZoneClick}
          responsiveStyles={responsiveStyles}
        />

        {/* Sub Header */}
        <TableSubHeader 
          dayLabels={dayLabels}
          responsiveStyles={responsiveStyles}
          hasOverrideValuesForDay={hasOverrideValuesForDay}
          hasOverrideValuesForKCPH={hasOverrideValuesForKCPH}
        />

        {/* Zone Data Rows */}
        {zoneData.map((row, rowIndex) => (
          <View key={row.id} style={[
            styles.dataRow,
            { minHeight: responsiveStyles.dataRowHeight },
          ]}>
            {/* Row-spanning highlight overlay for default inputs */}
            {highlightedRowIds.includes(row.id) && (
              <View style={[
                styles.rowHighlightOverlay,
                {
                  top: 0,
                  left: responsiveStyles.addZoneMaxWidth + 1, // Start after zone name (100px + 1px divider)
                  height: responsiveStyles.dataRowHeight,
                  // Width calculation:
                  // - 7 day columns (each 180px = default + override) = 1260px
                  // - 7 dividers between days (each 1px) = 7px
                  // - Add extra padding to ensure full coverage = 10px
                  // Total = 1277px to cover from Sun Default to Sat Override completely
                  width: (7.2 * responsiveStyles.dayGroupMinWidth) + 7 + 10,
                }
              ]} />
            )}

            {/* Zone Name */}
            <View style={[
              styles.zoneNameSection,
              { minWidth: responsiveStyles.addZoneMinWidth, maxWidth: responsiveStyles.addZoneMaxWidth }
            ]}>
              <Text style={[
                styles.zoneNameText,
                { fontSize: responsiveStyles.fontSize.subHeader }
              ]}>
                {row.zoneName}
              </Text>
            </View>

            <View style={styles.inputColumnDivider} />

            {/* Days with Default/Override inputs */}
            <View style={styles.daysGroup}>
              {dayLabels.map((day, index) => {
                const dayKey = day.toLowerCase() as keyof ZoneRow['percentages'];
                return (
                  <React.Fragment key={`day-input-${day}`}>
                    {index > 0 && <View style={styles.inputColumnDivider} />}
                    <View style={[
                      styles.dayGroup,
                      { minWidth: responsiveStyles.dayGroupMinWidth }
                    ]}>
                      <View style={styles.dayInputsContainer}>
                        <View style={styles.subColumn}>
                          <View style={styles.inputContainer}>
                            <TextInput
                              id={`default-${row.id}-${dayKey}`}
                              style={[
                                styles.input,
                                styles.defaultInput,
                                {
                                  width: responsiveStyles.inputWidth,
                                  height: responsiveStyles.inputHeight,
                                  fontSize: responsiveStyles.fontSize.input,
                                  color: !hasOverrideValuesForDay(dayKey) ? '#0061C7' : '#000000',
                                }
                              ]}
                              value={formatDisplayValue(row.percentages[dayKey].default)}
                              onChangeText={(value) => updatePercentage(row.id, dayKey, 'default', value)}
                              placeholderTextColor="#999"
                              editable={false}
                              pointerEvents="none"
                            />
                          </View>
                        </View>
                        <View style={styles.subColumnDivider} />
                        <View style={styles.subColumn}>
                          <View style={styles.inputContainer}>
                            <TextInput
                              id={`override-${row.id}-${dayKey}`}
                              style={[
                                styles.input,
                                styles.overrideInput,
                                row.__changed && row.__changed[dayKey] ? { fontWeight: 'bold' } : { fontWeight: '400' },
                                {
                                  width: responsiveStyles.inputWidth,
                                  height: responsiveStyles.inputHeight,
                                  fontSize: responsiveStyles.fontSize.input,
                                  backgroundColor: disabled ? '#f0f0f0' : undefined,
                                  color: hasOverrideValuesForDay(dayKey) ? '#0061C7' : '#000000',
                                },
                                darkOverrideInputStyle,
                              ]}
                              value={formatDisplayValue(row.percentages[dayKey].override)}
                              onFocus={disabled ? undefined : () => {
                                setZoneData(prev => prev.map(r => r.id === row.id ? { ...r, __focused: { ...(r.__focused || {}), [dayKey]: true } } : r));
                              }}
                              onBlur={disabled ? undefined : () => {
                                handlePercentageBlur(row.id, dayKey, 'override', row.percentages[dayKey].override);
                                setZoneData(prev => prev.map(r => r.id === row.id ? { ...r, __focused: { ...(r.__focused || {}), [dayKey]: false } } : r));
                              }}
                              onChangeText={disabled ? undefined : (value) => updatePercentage(row.id, dayKey, 'override', value)}
                              placeholderTextColor="#999"
                              keyboardType="decimal-pad"
                              editable={!disabled}
                              pointerEvents={disabled ? 'none' : undefined}
                            />
                          </View>
                        </View>
                      </View>
                    </View>
                  </React.Fragment>
                );
              })}
            </View>

            {/* Restore Column */}
            <View style={[
              styles.restoreColumnGroup,
              { minWidth: responsiveStyles.dayGroupMinWidth }
            ]}>
              <View style={styles.restoreColumnContainer}>
                <DLOButton
                  text="Restore"
                  onPress={() => {
                    if (disabled) return;
                    // Add this row to highlighted rows (preserve existing highlights)
                    setHighlightedRowIds(prev => prev.includes(row.id) ? prev : [...prev, row.id]);
                    
                    // Restore all days for this specific zone/row
                    updateZoneData(prev => prev.map(r => {
                      if (r.id === row.id) {
                        const updatedPercentages = { ...r.percentages };
                        dayLabels.forEach(day => {
                          const dayKey = day.toLowerCase() as keyof ZoneRow['percentages'];
                          updatedPercentages[dayKey] = {
                            ...updatedPercentages[dayKey],
                            override: updatedPercentages[dayKey].default,
                          };
                        });
                        return {
                          ...r,
                          percentages: updatedPercentages,
                          kcph: {
                            ...r.kcph,
                            override: r.kcph.default,
                          },
                        };
                      }
                      return r;
                    }));
                    setIsSaved(false);
                    setHasUserMadeChanges(true);
                    
                    // Highlight persists until save or new data loads
                  }}
                  size="small"
                  variant="primary"
                  style={styles.restoreButton}
                  disabled={disabled}
                />
                <View style={styles.restoreSingleBadgeContainer}>
                  {(() => {
                    // Calculate sum of override values for this specific zone/row
                    let zoneOverrideTotal = 0;
                    let hasAnyOverride = false;
                    
                    dayLabels.forEach(day => {
                      const dayKey = day.toLowerCase() as keyof ZoneRow['percentages'];
                      const defaultValue = parseFloat(row.percentages[dayKey].default.replace('%', '')) || 0;
                      const overrideValue = parseFloat(row.percentages[dayKey].override.replace('%', '')) || 0;
                      
                      // // Check if there's an actual override (different from default)
                      // if (Math.abs(defaultValue - overrideValue) > 0.01) {
                      //   hasAnyOverride = true;
                      //   // If default is 0 and override has value, add 100 + override value
                      //   if (Math.abs(defaultValue) < 0.01 && Math.abs(overrideValue) > 0.01) {
                      //     zoneOverrideTotal += 100 + overrideValue;
                      //   } else {
                      //     zoneOverrideTotal += overrideValue;
                      //   }
                      // } else {
                        // No override, use the actual value
                        zoneOverrideTotal += overrideValue;
                      // }
                    });
                    
                    // Check if all default and override values are the same for this row (all zeros)
                    const allDefaultsMatchOverrides = dayLabels.every(day => {
                      const dayKey = day.toLowerCase() as keyof ZoneRow['percentages'];
                      const defaultValue = parseFloat(row.percentages[dayKey].default.replace('%', '')) || 0;
                      const overrideValue = parseFloat(row.percentages[dayKey].override.replace('%', '')) || 0;
                      return Math.abs(defaultValue - overrideValue) < 0.01;
                    });
                    
                    // If all defaults match overrides (all zeros case), display as 100%
                    const displayTotal = allDefaultsMatchOverrides ? 100.0 : zoneOverrideTotal;
                    
                    // isCorrect is true if sum equals 100 OR if all defaults match overrides (all zeros case)
                    const isCorrect = Math.abs(zoneOverrideTotal - 100) < 0.01 || allDefaultsMatchOverrides;
                    
                    // If active tab is actual volume data, use blue border, white fill, blue bold text
                    const isActualVolumeTab = title?.toUpperCase().includes('ACTUAL');
                    return (
                      <View style={
                        isActualVolumeTab
                          ? [styles.restoreBadge, { borderColor: isDark ? '#3D8BFF' : '#005CBC', backgroundColor: isDark ? colors.background.elevated : '#fff', borderWidth: 2 }]
                          : [styles.restoreBadge, isCorrect ? styles.restoreBadgeGreen : styles.restoreBadgeRed]
                      }>
                        <Text style={
                          isActualVolumeTab
                            ? [styles.restoreBadgeText, { color: '#005CBC', fontWeight: 'bold' }]
                            : [styles.restoreBadgeText, isCorrect ? styles.restoreBadgeTextGreen : styles.restoreBadgeTextRed]
                        }>
                          {displayAsPercentage ? `${displayTotal.toFixed(1)}%` : displayTotal.toFixed(1)}
                        </Text>
                      </View>
                    );
                  })()}
                </View>
              </View>
            </View>

            <View style={styles.inputColumnDivider} />

            {/* KCPH Column */}
            <View style={[
              styles.kcphDataGroup,
              { minWidth: responsiveStyles.kcphMinWidth }
            ]}>
              <View style={styles.kcphInputsContainer}>
                <View style={styles.subColumn}>
                  <View style={styles.inputContainer}>
                    <TextInput
                      id={`kcph-default-${row.id}`}
                      style={[
                        styles.input,
                        styles.defaultInput,
                        {
                          width: responsiveStyles.inputWidth,
                          height: responsiveStyles.inputHeight,
                          fontSize: responsiveStyles.fontSize.input,
                          color: !hasOverrideValuesForKCPH() ? '#0061C7' : '#000000',
                        }
                      ]}
                      value={formatNumberWithSeparators(row.kcph.default)}
                      onChangeText={(value) => updateKCPH(row.id, 'default', value)}
                      placeholderTextColor="#999"
                      editable={false}
                      pointerEvents="none"
                      keyboardType="decimal-pad"
                    />
                  </View>
                </View>
                <View style={styles.subColumnDivider} />
                <View style={styles.subColumn}>
                  <View style={styles.inputContainer}>
                    <TextInput
                      id={`kcph-override-${row.id}`}
                      style={[
                        styles.input,
                        styles.overrideInput,
                        row.__changed && row.__changed.kcph ? { fontWeight: 'bold' } : { fontWeight: '400' },
                        {
                          width: responsiveStyles.inputWidth,
                          height: responsiveStyles.inputHeight,
                          fontSize: responsiveStyles.fontSize.input,
                          backgroundColor: disabled ? '#f0f0f0' : undefined,
                          color: hasOverrideValuesForKCPH() ? '#0061C7' : '#000000',
                        },
                        darkOverrideInputStyle,
                      ]}
                      value={formatNumberWithSeparators(row.kcph.override)}
                      onChangeText={disabled ? undefined : (value) => updateKCPH(row.id, 'override', value)}
                      onBlur={disabled ? undefined : () => handleKCPHBlur(row.id, 'override', row.kcph.override)}
                      placeholderTextColor="#999"
                      keyboardType="decimal-pad"
                      editable={!disabled}
                      pointerEvents={disabled ? 'none' : undefined}
                    />
                  </View>
                </View>
              </View>
            </View>
          </View>
        ))}

        {/* Save Row */}
        <View style={[
          styles.saveRow,
          { minHeight: responsiveStyles.dataRowHeight },
        ]}>
          <View style={styles.saveRowContent}>
            <DLOButton
              text={context === 'schedule-settings' ? (externalIsSaved ? "Saved" : "Save") : (isSaved ? "Saved" : "Save")}
              onPress={disabled ? undefined : saveLocationSettings}
              size="medium"
              variant="primary"
              style={styles.saveButton}
              disabled={disabled || (context === 'schedule-settings' ? externalIsSaved : isSaved) || !isSaveEnabled}
              isLoading={context === 'schedule-settings' ? isLoading : isSaving}
            />
          </View>
        </View>
      </View>
      
      {/* Edit Zone Modal */}
      <EditZoneModal
        visible={isEditZoneModalVisible}
        availableZones={availableZones}
        onClose={handleModalCancel}
        onSave={handleModalSave}
        onZoneToggle={handleZoneToggle}
      />

      {/* Save Result Modal */}
      <SaveResultModal
        visible={showSaveModal}
        saveModalType={saveModalType}
        saveModalMessage={saveModalMessage}
        onClose={() => setShowSaveModal(false)}
      />
    </ScrollView>
  );
};

export default NonDSDVolumeZoneDistributionByDay;